// Composants génériques de la plateforme (cf. css/components.css).
// Minces : du markup + des tokens, AUCUNE logique métier ni vocabulaire
// « messagerie » — toute brique future (projets, calendrier…) les réutilise.
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Comportement « dialogue » accessible : focus piégé dans le conteneur,
 *  Échap ferme (en capture, sans déclencher les raccourcis globaux), et le
 *  focus revient à l'élément ouvreur à la fermeture. */
function useDialog(onClose: () => void) {
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const el = ref.current;
    (el?.querySelector<HTMLElement>(FOCUSABLE) ?? el)?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !el) return;
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      // Focus sorti ou aux bornes : on boucle dans le dialogue.
      if (el !== active && !el.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      opener?.focus();
    };
  }, []);
  return ref;
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "primary",
  small = false,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; small?: boolean }) {
  const cls = ["ui-btn", `ui-btn--${variant}`, small ? "ui-btn--sm" : "", className]
    .filter(Boolean)
    .join(" ");
  return <button type="button" {...props} className={cls} />;
}

export function Field({
  label,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label?: string }) {
  return (
    <label className="ui-field">
      {label && <span className="ui-field-label">{label}</span>}
      <input {...props} className="ui-field-input" />
    </label>
  );
}

/** Contrôle segmenté (choix exclusif court, ex. thème Auto/Clair/Sombre). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
  ariaLabel?: string;
}) {
  return (
    <div className="ui-segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`ui-segmented-item ${value === o.value ? "active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Liste encartée (style Réglages macOS). `header` rend le titre de section. */
export function List({ header, children }: { header?: ReactNode; children: ReactNode }) {
  return (
    <section>
      {header && <div className="ui-list-header">{header}</div>}
      <div className="ui-list">{children}</div>
    </section>
  );
}

/** Rangée de liste. Avec `onClick`, rendue en <button> (cible ≥ 44 pt). */
export function ListRow({
  leading,
  title,
  value,
  trailing,
  onClick,
  ariaLabel,
  selected,
}: {
  leading?: ReactNode;
  title: ReactNode;
  value?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
  /** Rangée sélectionnée (surlignage) — pour les listes de navigation. */
  selected?: boolean;
}) {
  const cls = `ui-list-row${selected ? " is-selected" : ""}`;
  const body = (
    <>
      {leading}
      <span className="ui-list-row__title">{title}</span>
      {value !== undefined && <span className="ui-list-row__value">{value}</span>}
      {trailing}
    </>
  );
  return onClick ? (
    <button type="button" className={cls} onClick={onClick} aria-label={ariaLabel}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** Modale centrée : voile cliquable, Échap, focus piégé puis rendu à l'ouvreur. */
export function Modal({
  title,
  onClose,
  children,
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useDialog(onClose);
  return (
    <div className="ui-modal-backdrop" onClick={onClose}>
      <div
        ref={ref}
        tabIndex={-1}
        className="ui-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h2 className="ui-modal-title">{title}</h2>}
        {children}
      </div>
    </div>
  );
}

/** Feuille basse (mobile) : voile + grabber, Échap + focus piégé, safe-area CSS. */
export function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useDialog(onClose);
  return (
    <div className="ui-modal-backdrop" onClick={onClose}>
      <div
        ref={ref}
        tabIndex={-1}
        className="ui-sheet"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ui-sheet-grabber" />
        {children}
      </div>
    </div>
  );
}

/** Menu contextuel ancré à un point (positions bornées par l'appelant).
 *  Clavier : flèches pour circuler entre les items, Échap pour fermer. */
export function Menu({
  x,
  y,
  onClose,
  children,
}: {
  x: number;
  y: number;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useDialog(onClose);
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (items.length === 0) return;
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === "ArrowDown"
        ? items[i < 0 ? 0 : (i + 1) % items.length]
        : items[i < 0 ? items.length - 1 : (i - 1 + items.length) % items.length];
    next?.focus();
  }
  return (
    <>
      <div
        className="ui-menu-backdrop"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={ref}
        tabIndex={-1}
        className="ui-menu"
        role="menu"
        style={{ left: x, top: y }}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </>
  );
}

export function MenuItem({
  danger = false,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }) {
  const cls = ["ui-menu-item", danger ? "ui-menu-item--danger" : "", className]
    .filter(Boolean)
    .join(" ");
  return <button type="button" role="menuitem" {...props} className={cls} />;
}

export function Badge({
  count,
  dot = false,
  neutral = false,
}: {
  count?: number | string;
  dot?: boolean;
  neutral?: boolean;
}) {
  const cls = ["ui-badge", dot ? "ui-badge--dot" : "", neutral ? "ui-badge--neutral" : ""]
    .filter(Boolean)
    .join(" ");
  return <span className={cls}>{dot ? null : count}</span>;
}

/** État vide : icône + phrase (ex. « Sélectionne une conversation »). */
export function EmptyState({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="ui-empty">
      {icon && <span className="ui-empty-icon">{icon}</span>}
      <p>{children}</p>
    </div>
  );
}

export function Spinner({ label = "Chargement…" }: { label?: string }) {
  return <span className="ui-spinner" role="status" aria-label={label} />;
}

/**
 * Loader pleine-zone, CENTRÉ et un peu joueur (#5) : trois points qui rebondissent
 * en cascade, comme l'indicateur « en train d'écrire… » d'une messagerie — clin
 * d'œil à l'OS. Pour les chargements de page/panneau (≠ le petit Spinner inline
 * des boutons). Respecte prefers-reduced-motion (cf .page-loader en CSS).
 */
export function PageLoader({ label = "Chargement…" }: { label?: string }) {
  return (
    <div className="page-loader" role="status" aria-label={label}>
      <span className="page-loader-dots" aria-hidden="true">
        <span className="page-loader-dot" />
        <span className="page-loader-dot" />
        <span className="page-loader-dot" />
      </span>
      <span className="page-loader-label">{label}</span>
    </div>
  );
}

/** Bloc squelette : dimensionné par l'appelant (largeur/hauteur en style). */
export function Skeleton({ width, height = 14 }: { width?: number | string; height?: number }) {
  return <span className="ui-skeleton" style={{ width, height, display: "inline-block" }} />;
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="ui-error" role="alert">
      <span className="ui-error-msg">{message}</span>
      {onRetry && (
        <Button variant="secondary" small onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}

export function Banner({
  tone = "info",
  children,
}: {
  tone?: "info" | "danger" | "neutral";
  children: ReactNode;
}) {
  const cls = ["ui-banner", tone !== "info" ? `ui-banner--${tone}` : ""].filter(Boolean).join(" ");
  return (
    <div className={cls} aria-live="polite">
      {children}
    </div>
  );
}
